import React, { useEffect, useState } from 'react';

/** Installer-build progress + download link.
 *
 * Mount when an installer-build POST returned an `installer_task_id`;
 * pass the task id (and optionally the initial status from the POST
 * response). The component owns the polling — useInstallerStatus hook
 * fires every 2 s while the task is `pending`, stops on `done`/`error`.
 *
 * Used by:
 *   - AddNodeModal (Add Node → standalone substrate flow)
 *   - NodeDetail's "Build installer for existing node" modal
 *
 * Shared so the UX stays identical across the two entry points.
 *
 * onStatusChange (optional): called with the build status string
 * ('pending' | 'done' | 'error') whenever it changes, so the parent
 * modal can gate its "Done" button on the build actually finishing —
 * otherwise the panel's completion state is invisible to it. */
export function InstallerBuildPanel({ taskId, initialStatus, onStatusChange }) {
  const status = useInstallerStatus(taskId, initialStatus);
  const s = (status && status.status) || 'pending';

  useEffect(() => {
    if (onStatusChange) onStatusChange(s);
    // Only re-notify when the status string itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s]);

  const baseStyle = {
    marginTop: 16, padding: 12, border: '1px solid #ddd',
    borderRadius: 4, background: '#fafafa', fontSize: '0.85rem',
  };

  if (s === 'pending') {
    return (
      <div style={baseStyle}>
        <strong>Building installer…</strong>
        <div style={{ color: '#666', marginTop: 4 }}>
          Fetching bundle and stamping per-recipient files. Typically 10–60 s.
        </div>
      </div>
    );
  }
  if (s === 'done') {
    const fname = (status && status.artifact_basename) || 'installer.run';
    return (
      <div style={{ ...baseStyle, background: '#e6f4ea', borderColor: '#a8d5b3' }}>
        <strong style={{ color: '#1e8e3e' }}>Installer ready</strong>
        <div style={{ marginTop: 8 }}>
          <a href={`/api/installer/${taskId}/download`}
             download={fname}
             style={downloadBtnStyle}>
            Download {fname}
          </a>
        </div>
        <small style={{ color: '#666', display: 'block', marginTop: 6 }}>
          Send this <code>.run</code> to the recipient. They run it once
          ({status && status.flavor === 'bare-metal' ? 'as root' : 'with docker available'});
          the bundle has identity + bearer pre-stamped.
        </small>
      </div>
    );
  }
  // error
  return (
    <div style={{ ...baseStyle, background: '#fdecea', borderColor: '#f5b7b0' }}>
      <strong style={{ color: '#c00' }}>Installer build failed</strong>
      <div style={{ color: '#444', marginTop: 4 }}>{(status && status.error_msg) || 'unknown error'}</div>
      {status && status.stderr_tail && (
        <pre style={{
          marginTop: 8, fontSize: '0.75rem', background: '#fff',
          padding: 8, borderRadius: 3, maxHeight: 160, overflow: 'auto',
        }}>{status.stderr_tail}</pre>
      )}
      <small style={{ color: '#666', display: 'block', marginTop: 6 }}>
        Hub-side state is unaffected. Re-trigger via the "Build installer"
        button on the node detail panel, or run <code>bin/build-installer.sh</code>
        on the operator host.
      </small>
    </div>
  );
}

/** Poll /api/installer/{task_id} every 2 s until status leaves
 *  'pending'. Returns the latest status dict (or the initial one if
 *  taskId is null). Cancels cleanly on unmount or task-id change. */
export function useInstallerStatus(taskId, initial) {
  const [status, setStatus] = useState(initial || null);
  useEffect(() => {
    if (!taskId) return undefined;
    if (status && status.status !== 'pending') return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`/api/installer/${taskId}`);
        if (cancelled) return;
        if (!r.ok) {
          setStatus({ status: 'error', error_msg: `HTTP ${r.status}` });
          return;
        }
        const parsed = await r.json();
        if (cancelled) return;
        setStatus(parsed);
      } catch (e) {
        if (!cancelled) setStatus({ status: 'error', error_msg: String(e) });
      }
    };
    const id = setInterval(tick, 2000);
    tick();  // also fire immediately
    return () => { cancelled = true; clearInterval(id); };
    // Only re-run when the taskId or the pending-vs-not transition changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, status && status.status]);
  return status;
}

const downloadBtnStyle = {
  padding: '8px 16px', background: '#5fa67a', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
  textDecoration: 'none', display: 'inline-block',
};
